import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Plus, Trash2, Play, X, FileText, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import {
  artifacts as artifactsApi,
  promptSlots as slotsApi,
  promptTestSets as testSetsApi,
  promptVersions as versionsApi,
  runTestSet,
  sandboxRuns as sandboxRunsApi,
  testSetRuns as testSetRunsApi,
  type Artifact,
  type PromptSlot,
  type PromptTestSet,
  type PromptVersion,
  type TestSetRunResult,
  type TestSetRun,
} from '../../api'

export default function TestSets() {
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const [slotId, setSlotId] = useState<number | null>(null)
  const [tsId, setTsId] = useState<number | null>(null)

  const sets = useQuery({
    queryKey: ['promptTestSets', slotId],
    queryFn: () => (slotId ? testSetsApi.bySlot(slotId) : testSetsApi.list()),
  })

  return (
    <div className="grid h-full grid-cols-[220px_280px_1fr] gap-px bg-[color:var(--me-border)]">
      <SlotsRail
        slots={slots.data ?? []}
        selectedId={slotId}
        onSelect={(id) => {
          setSlotId(id)
          setTsId(null)
        }}
      />
      <SetsList
        slot={slots.data?.find((s) => s.id === slotId) ?? null}
        sets={sets.data ?? []}
        selectedId={tsId}
        onSelect={setTsId}
        onCreated={() => sets.refetch()}
        defaultSlotId={slotId}
      />
      <SetDetail
        set={sets.data?.find((s) => s.id === tsId) ?? null}
        onChanged={() => sets.refetch()}
        onDeleted={() => {
          setTsId(null)
          sets.refetch()
        }}
      />
    </div>
  )
}

function SlotsRail({ slots, selectedId, onSelect }: { slots: PromptSlot[]; selectedId: number | null; onSelect: (id: number | null) => void }) {
  return (
    <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
      <div className="me-section-h">Slots · {slots.length}</div>
      <button
        onClick={() => onSelect(null)}
        data-selected={selectedId === null ? 'true' : undefined}
        className="me-row mb-0.5"
      >
        <span className="font-medium me-text">All test sets</span>
      </button>
      {slots.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          data-selected={selectedId === s.id ? 'true' : undefined}
          className="me-row mb-0.5"
        >
          <div className="flex items-center gap-2">
            <span className="me-chip">L{s.level}</span>
            <span className="font-medium me-text truncate">{s.name}</span>
            {s.is_sandbox && (
              <span className="me-chip me-chip-stale" title="Sandbox slot — no DAG impact">sb</span>
            )}
          </div>
        </button>
      ))}
    </aside>
  )
}

function SetsList({
  slot,
  sets,
  selectedId,
  onSelect,
  onCreated,
  defaultSlotId,
}: {
  slot: PromptSlot | null
  sets: PromptTestSet[]
  selectedId: number | null
  onSelect: (id: number) => void
  onCreated: () => void
  defaultSlotId: number | null
}) {
  const [creating, setCreating] = useState(false)
  return (
    <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
      <div className="me-section-h flex items-center justify-between">
        <span>Test sets {slot ? `for ${slot.name}` : '(all)'} · {sets.length}</span>
        {defaultSlotId && (
          <button onClick={() => setCreating(true)} className="me-btn me-btn-outline" title="New test set">
            <Plus size={12} />
          </button>
        )}
      </div>
      {creating && defaultSlotId && (
        <CreateForm
          slotId={defaultSlotId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            onCreated()
          }}
        />
      )}
      {sets.length === 0 && !creating && (
        <div className="mt-3 text-[12px] me-text-mute italic">
          No test sets yet. {slot ? 'Create one to start dry-running this prompt against curated artifacts.' : 'Pick a slot above to create one.'}
        </div>
      )}
      {sets.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          data-selected={selectedId === s.id ? 'true' : undefined}
          className="me-row mb-0.5"
        >
          <div className="font-medium me-text">{s.name}</div>
          <div className="me-mono mt-0.5 text-[11px] me-text-mute">
            {s.slot_name} · {s.artifact_count} artifact{s.artifact_count === 1 ? '' : 's'}
          </div>
        </button>
      ))}
    </aside>
  )
}

function CreateForm({ slotId, onClose, onCreated }: { slotId: number; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const create = useMutation({
    mutationFn: () => testSetsApi.create({ slot: slotId, name, description }),
    onSuccess: () => onCreated(),
  })
  return (
    <div className="me-card mt-2 p-3">
      <div className="me-section-h mb-1 flex items-center justify-between">
        <span>New test set</span>
        <button onClick={onClose} className="me-btn me-btn-outline" title="Cancel">
          <X size={12} />
        </button>
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name (e.g. baseline 10 emails)"
        className="me-input mb-2 w-full"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        className="me-input mb-2 w-full"
        rows={2}
      />
      <button
        disabled={!name.trim() || create.isPending}
        onClick={() => create.mutate()}
        className="me-btn me-btn-primary w-full"
      >
        {create.isPending ? 'Creating…' : 'Create test set'}
      </button>
      {create.isError && (
        <div className="mt-2 text-[11px] text-[color:var(--me-stale)]">
          {(create.error as Error).message}
        </div>
      )}
    </div>
  )
}

function SetDetail({ set, onChanged, onDeleted }: { set: PromptTestSet | null; onChanged: () => void; onDeleted: () => void }) {
  const [tab, setTab] = useState<'artifacts' | 'expected' | 'run' | 'history'>('artifacts')
  if (!set) {
    return (
      <div className="me-scroll overflow-auto p-8 text-[13px] me-text-mute">
        Select a test set on the left, or create one for the active slot.
      </div>
    )
  }
  return (
    <div className="me-scroll flex flex-col overflow-hidden bg-[color:var(--me-bg)]">
      <header className="border-b me-border bg-[color:var(--me-surface)] p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[15px] font-semibold me-text">{set.name}</div>
            <div className="me-mono mt-0.5 text-[11px] me-text-mute">
              {set.slot_name} · {set.artifact_count} artifacts
            </div>
            {set.description && <div className="mt-2 text-[12px] me-text-dim">{set.description}</div>}
          </div>
          <DeleteButton id={set.id} onDeleted={onDeleted} />
        </div>
        <nav className="mt-3 flex gap-0.5">
          {(['artifacts', 'expected', 'run', 'history'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-selected={tab === t ? 'true' : undefined}
              className="me-tab"
            >
              {t === 'artifacts' ? 'Artifacts'
                : t === 'expected' ? 'Expected outputs'
                : t === 'run' ? 'Run against version'
                : 'History'}
            </button>
          ))}
        </nav>
      </header>
      <div className="me-scroll flex-1 overflow-auto p-4">
        {tab === 'artifacts' && <ArtifactsPicker set={set} onChanged={onChanged} />}
        {tab === 'expected' && <ExpectedEditor set={set} onChanged={onChanged} />}
        {tab === 'run' && <RunPanel set={set} />}
        {tab === 'history' && <HistoryPanel set={set} />}
      </div>
    </div>
  )
}

function HistoryPanel({ set }: { set: PromptTestSet }) {
  // List every TestSetRun against this set newest-first. Click a row
  // to expand and see the per-artifact SandboxRun children with full
  // input/output. Persisted history means iterations don't get lost.
  const runs = useQuery({
    queryKey: ['testSetRuns', set.id],
    queryFn: () => testSetRunsApi.list({ test_set: set.id, limit: 200 }),
  })
  // Set of expanded run IDs (multi-expand so user can compare runs
  // side-by-side, and the expand-all button has something to do).
  const [openSet, setOpenSet] = useState<Set<number>>(new Set())
  const toggle = (id: number) => setOpenSet((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[11px] me-text-mute italic">
          Each run snapshots the prompt version + per-artifact responses.
          Persisted to TestSetRun (header) + SandboxRun (per-artifact rows).
          Production state is never touched.
        </div>
        {(runs.data ?? []).length > 0 && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setOpenSet(new Set((runs.data ?? []).map((r) => r.id)))}
              disabled={openSet.size === (runs.data ?? []).length}
              className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
              title="Expand all runs"
            >
              Expand all
            </button>
            <button
              onClick={() => setOpenSet(new Set())}
              disabled={openSet.size === 0}
              className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
              title="Collapse all runs"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>
      {(runs.data ?? []).length === 0 && (
        <div className="me-card p-3 text-[12px] me-text-mute italic">
          No runs yet. Use the Run tab to execute the set against a prompt version.
        </div>
      )}
      <ul className="space-y-1">
        {(runs.data ?? []).map((r) => (
          <TestSetRunRow
            key={r.id}
            run={r}
            expanded={openSet.has(r.id)}
            onToggle={() => toggle(r.id)}
          />
        ))}
      </ul>
    </div>
  )
}

function TestSetRunRow({
  run, expanded, onToggle,
}: { run: TestSetRun; expanded: boolean; onToggle: () => void }) {
  const sb = useQuery({
    queryKey: ['sandboxRuns', 'test-set-run', run.id],
    queryFn: () => sandboxRunsApi.list({ test_set_run: run.id, limit: 500 }),
    enabled: expanded,
  })
  // Per-TestSetRun expand state for the SandboxRun (per-artifact) child
  // rows — controlled (not <details>) so the per-run expand-all button
  // can drive every child at once. Cleared on collapse so re-opening is
  // consistent.
  const [openChildren, setOpenChildren] = useState<Set<number>>(new Set())
  const toggleChild = (id: number) => setOpenChildren((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const statusCls =
    run.status === 'completed' ? 'me-chip me-chip-fresh'
    : run.status === 'failed' ? 'me-chip me-chip-urgent'
    : run.status === 'partial' ? 'me-chip me-chip-stale'
    : 'me-chip'
  const elapsed = run.completed_at
    ? Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 1000)
    : null
  const childRows = sb.data ?? []
  return (
    <li className="me-card p-2.5">
      <button onClick={onToggle} className="w-full text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="me-mono text-[11.5px] inline-flex items-center gap-1">
            {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {new Date(run.started_at).toLocaleString()}
            {' · v'}{run.prompt_version_number ?? '?'}
          </span>
          <span className={statusCls}>{run.status}</span>
        </div>
        <div className="me-mono mt-0.5 text-[10px] me-text-mute">
          {run.artifact_count} artifact{run.artifact_count === 1 ? '' : 's'}
          {' · '}{run.success_count} ok
          {run.error_count > 0 && <> · <span className="text-[color:var(--me-stale)]">{run.error_count} error</span></>}
          {elapsed != null && ` · ${elapsed}s`}
          {run.created_by_username && ` · by ${run.created_by_username}`}
        </div>
      </button>
      {expanded && (
        <div className="mt-2 space-y-1">
          {sb.isLoading && <div className="text-[11px] me-text-mute">Loading per-artifact rows…</div>}
          {!sb.isLoading && childRows.length > 0 && (
            <div className="flex items-center gap-1 pb-1">
              <button
                onClick={(e) => { e.stopPropagation(); setOpenChildren(new Set(childRows.map((s) => s.id))) }}
                disabled={openChildren.size === childRows.length}
                className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
                title="Expand all per-artifact rows in this run"
              >
                Expand all
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setOpenChildren(new Set()) }}
                disabled={openChildren.size === 0}
                className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
                title="Collapse all per-artifact rows in this run"
              >
                Collapse all
              </button>
            </div>
          )}
          {childRows.map((s) => (
            <div key={s.id} className="border-t me-border pt-1.5">
              <button
                onClick={(e) => { e.stopPropagation(); toggleChild(s.id) }}
                className="w-full text-left cursor-pointer me-mono text-[11px] inline-flex items-center gap-1"
                type="button"
              >
                {openChildren.has(s.id) ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <span className={s.status === 'success' ? 'me-chip me-chip-fresh mr-1' : 'me-chip me-chip-urgent mr-1'}>{s.status}</span>
                {s.artifact_composite_id || `artifact #${s.artifact}`} · {s.artifact_subject}
              </button>
              {openChildren.has(s.id) && (
                <>
              {/* Output left, Input right — output is the answer (usually
               *  short) so it reads first; input goes on the right where
               *  the long context lives. */}
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>
                  <div className="me-section-h !mb-0.5">Output</div>
                  <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[10.5px] leading-relaxed max-h-48 overflow-auto">{s.output_text || '(empty)'}</pre>
                </div>
                <div>
                  <div className="me-section-h !mb-0.5">Input</div>
                  <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-2 text-[10.5px] leading-relaxed max-h-48 overflow-auto">{s.rendered_input || '(empty)'}</pre>
                </div>
              </div>
                </>
              )}
            </div>
          ))}
          {sb.isSuccess && childRows.length === 0 && (
            <div className="text-[11px] me-text-mute italic">No per-artifact rows recorded.</div>
          )}
        </div>
      )}
    </li>
  )
}

function DeleteButton({ id, onDeleted }: { id: number; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const remove = useMutation({
    mutationFn: () => testSetsApi.remove(id),
    onSuccess: () => onDeleted(),
  })
  if (!confirming) {
    return (
      <button onClick={() => setConfirming(true)} className="me-btn me-btn-outline" title="Delete">
        <Trash2 size={12} />
      </button>
    )
  }
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => remove.mutate()} className="me-btn me-btn-primary" disabled={remove.isPending}>
        Confirm delete
      </button>
      <button onClick={() => setConfirming(false)} className="me-btn me-btn-outline">
        Cancel
      </button>
    </div>
  )
}

function ArtifactsPicker({ set, onChanged }: { set: PromptTestSet; onChanged: () => void }) {
  const [filter, setFilter] = useState('')
  // Server-side search: ?search=<q> hits indexed subject / from / composite_id
  // / body so we don't need to load the whole corpus into the browser. When
  // the filter is empty we still fetch the first 500 as a browse pool. Scoped
  // to the test set's tenant for cross-tenant platform-admin sessions.
  const all = useQuery({
    queryKey: ['artifacts', 'picker', set.client, filter.trim()],
    queryFn: () => artifactsApi.list({
      limit: 500, client: set.client,
      ...(filter.trim() ? { search: filter.trim() } : {}),
    }),
  })
  const update = useMutation({
    mutationFn: (artifacts: number[]) => testSetsApi.update(set.id, { artifacts }),
    onSuccess: () => onChanged(),
  })
  const memberSet = useMemo(() => new Set(set.artifacts), [set.artifacts])
  // Members may not be in the current search results — resolve by ID for the
  // ones that ARE present, show a placeholder count for any not loaded.
  const memberArtifacts = useMemo(() => {
    const byId = new Map((all.data ?? []).map((a) => [a.id, a]))
    return set.artifacts.map((id) => byId.get(id)).filter((a): a is Artifact => !!a)
  }, [all.data, set.artifacts])
  // Server already filtered; no client-side trimming needed.
  const visible = all.data ?? []

  function toggle(id: number) {
    const next = new Set(memberSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    update.mutate(Array.from(next))
  }

  return (
    <div className="grid h-full grid-cols-[1fr_1fr] gap-3">
      <div className="me-card flex flex-col overflow-hidden">
        <div className="border-b me-border p-2">
          <div className="me-section-h">In this test set · {set.artifacts.length}</div>
        </div>
        <div className="me-scroll flex-1 overflow-auto p-2">
          {set.artifacts.length === 0 ? (
            <div className="text-[12px] me-text-mute italic">No artifacts yet — add some from the right.</div>
          ) : memberArtifacts.length === 0 ? (
            <div className="text-[12px] me-text-mute italic">Loading members…</div>
          ) : (
            memberArtifacts.map((a) => <ArtifactRow key={a.id} a={a} member onToggle={() => toggle(a.id)} />)
          )}
        </div>
      </div>
      <div className="me-card flex flex-col overflow-hidden">
        <div className="border-b me-border p-2">
          <div className="me-section-h mb-1">Available artifacts</div>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by subject, from, type, composite id…"
            className="me-input w-full"
          />
        </div>
        <div className="me-scroll flex-1 overflow-auto p-2">
          {all.isLoading ? (
            <div className="text-[12px] me-text-mute">Loading…</div>
          ) : (
            visible.map((a) => (
              <ArtifactRow key={a.id} a={a} member={memberSet.has(a.id)} onToggle={() => toggle(a.id)} />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

function ArtifactRow({ a, member, onToggle }: { a: Artifact; member: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="me-row mb-0.5 w-full text-left"
      data-selected={member ? 'true' : undefined}
    >
      <div className="flex items-center gap-2">
        <FileText size={12} className="me-text-mute" />
        <span className="me-mono text-[11px] me-text-mute shrink-0">
          {a.composite_id || `#${a.client_seq}`}
        </span>
        <span className="truncate font-medium me-text">{a.subject || '(no subject)'}</span>
      </div>
      <div className="me-mono mt-0.5 truncate text-[11px] me-text-mute">
        {a.artifact_type} · {a.from_name || a.from_email || 'unknown'}
        {a.visibility === 'restricted' && ' · restricted'}
      </div>
    </button>
  )
}

function ExpectedEditor({ set, onChanged }: { set: PromptTestSet; onChanged: () => void }) {
  const [text, setText] = useState(JSON.stringify(set.expected_outputs ?? {}, null, 2))
  const [err, setErr] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (parsed: Record<string, unknown>) => testSetsApi.update(set.id, { expected_outputs: parsed }),
    onSuccess: () => onChanged(),
  })

  function handleSave() {
    try {
      const parsed = JSON.parse(text)
      setErr(null)
      save.mutate(parsed)
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  return (
    <div className="me-card flex h-full flex-col p-3">
      <div className="me-section-h mb-2">Expected outputs (golden)</div>
      <p className="mb-3 text-[12px] me-text-mute">
        JSON keyed by artifact id. When set, dry-runs flag rows whose parsed output doesn't match.
        Example: <code className="me-mono">{`{"42": {"is_personal": false}}`}</code>
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        className="me-input me-mono flex-1 text-[12px]"
      />
      {err && (
        <div className="mt-2 text-[11px] text-[color:var(--me-stale)]">
          <AlertTriangle size={11} className="mr-1 inline" />
          {err}
        </div>
      )}
      <div className="mt-2 flex justify-end">
        <button disabled={save.isPending} onClick={handleSave} className="me-btn me-btn-primary">
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function RunPanel({ set }: { set: PromptTestSet }) {
  const versions = useQuery({
    queryKey: ['promptVersions', set.slot],
    queryFn: () => versionsApi.bySlot(set.slot),
  })
  const [versionId, setVersionId] = useState<number | null>(null)
  const [results, setResults] = useState<TestSetRunResult[] | null>(null)
  // Set of artifact_ids currently expanded. Lifting this state up out of
  // ResultRow lets the expand-all / collapse-all buttons drive every row
  // at once.
  const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set())
  const toggleRow = (id: number) => setExpandedSet((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const run = useMutation({
    mutationFn: (vId: number) => runTestSet(vId, set.id),
    onSuccess: (data) => {
      setResults(data.results)
      setExpandedSet(new Set())  // reset on each fresh run
    },
  })

  // Default to production version once loaded
  if (versionId === null && versions.data?.length) {
    const prod = versions.data.find((v) => v.labels.includes('production')) ?? versions.data[0]
    if (prod) setVersionId(prod.id)
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="me-card flex items-end gap-3 p-3">
        <div className="flex-1">
          <div className="me-section-h mb-1">Prompt version</div>
          <select
            value={versionId ?? ''}
            onChange={(e) => setVersionId(Number(e.target.value) || null)}
            className="me-input w-full"
          >
            {(versions.data ?? []).map((v: PromptVersion) => (
              <option key={v.id} value={v.id}>
                v{v.version}
                {v.labels.length ? ` [${v.labels.join(',')}]` : ''}
                {v.commit_message ? ` — ${v.commit_message}` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          disabled={!versionId || run.isPending || set.artifacts.length === 0}
          onClick={() => versionId && run.mutate(versionId)}
          className="me-btn me-btn-primary inline-flex items-center gap-1"
        >
          <Play size={12} />
          {run.isPending ? 'Running…' : `Run against ${set.artifacts.length} artifact${set.artifacts.length === 1 ? '' : 's'}`}
        </button>
      </div>

      {set.artifacts.length === 0 && (
        <div className="me-card p-3 text-[12px] me-text-mute italic">
          Add artifacts to this test set on the Artifacts tab before running.
        </div>
      )}

      {run.isError && (
        <div className="me-card border-[color:var(--me-stale)] p-3 text-[12px] text-[color:var(--me-stale)]">
          {(run.error as Error).message}
        </div>
      )}

      {results && (
        <div className="me-card flex flex-1 flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b me-border p-2">
            <div className="me-section-h !mb-0">Results · {results.length}</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setExpandedSet(new Set(results.map((r) => r.artifact_id)))}
                disabled={results.length === 0 || expandedSet.size === results.length}
                className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
                title="Expand all rows"
              >
                Expand all
              </button>
              <button
                onClick={() => setExpandedSet(new Set())}
                disabled={expandedSet.size === 0}
                className="me-btn me-btn-outline !text-[10px] !px-2 !py-1 disabled:opacity-40"
                title="Collapse all rows"
              >
                Collapse all
              </button>
            </div>
          </div>
          <div className="me-scroll flex-1 overflow-auto">
            {results.map((r) => (
              <ResultRow
                key={r.artifact_id}
                r={r}
                open={expandedSet.has(r.artifact_id)}
                onToggle={() => toggleRow(r.artifact_id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ResultRow({ r, open, onToggle }: { r: TestSetRunResult; open: boolean; onToggle: () => void }) {
  const status =
    r.matches_expected === true
      ? { label: 'matches expected', cls: 'text-[color:var(--me-fresh)]' }
      : r.matches_expected === false
        ? { label: 'differs from expected', cls: 'text-[color:var(--me-stale)]' }
        : { label: 'no expected set', cls: 'me-text-mute' }
  return (
    <div className="border-b me-border">
      <button
        onClick={onToggle}
        className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-3 p-2 text-left hover:bg-[color:var(--me-surface-hover)]"
      >
        <div>
          <div className="me-mono text-[11px] me-text-mute">artifact #{r.artifact_id}</div>
          <div className="truncate text-[13px] me-text">{r.artifact_subject || '(no subject)'}</div>
        </div>
        <div className="me-mono text-[11px] me-text-mute">
          {r.input_tokens}+{r.output_tokens}t · {r.latency_ms}ms
        </div>
        <div className={`me-mono text-[11px] ${status.cls}`}>{status.label}</div>
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-3 p-3">
          {/* Output left, Input right — output reads first, input is
           *  reference. Expected (if any) sits under output for
           *  side-by-side comparison with the actual result. */}
          <div>
            <div className="me-section-h mb-1">Output</div>
            <pre className="me-card me-scroll me-mono max-h-72 overflow-auto p-2 text-[11px] me-text whitespace-pre-wrap">
              {typeof r.output_parsed === 'object' && r.output_parsed !== null
                ? JSON.stringify(r.output_parsed, null, 2)
                : r.output_text}
            </pre>
            {r.expected !== null && r.expected !== undefined && (
              <>
                <div className="me-section-h mt-2 mb-1">Expected</div>
                <pre className="me-card me-scroll me-mono max-h-40 overflow-auto p-2 text-[11px] me-text-dim whitespace-pre-wrap">
                  {JSON.stringify(r.expected, null, 2)}
                </pre>
              </>
            )}
          </div>
          <div>
            <div className="me-section-h mb-1">Rendered input</div>
            <pre className="me-card me-scroll me-mono max-h-72 overflow-auto p-2 text-[11px] me-text-dim whitespace-pre-wrap">
              {r.rendered_input}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}
